import { Directive, ElementRef, HostListener, afterNextRender, effect, signal, input, inject } from "@angular/core";

/** With `fitToViewport`, how much of the panel stays on screen when it is dragged toward the bottom. */
const MIN_VISIBLE_HEIGHT = 240;
/** With `fitToViewport`, the panel opens no lower than this fraction of the viewport so its content has room. */
const MAX_INITIAL_TOP_FRACTION = 0.15;
const INTERACTIVE_SELECTOR =
    "button, a, [role='button'], input, select, textarea, [contenteditable='true'], [tabindex]";

/**
 * Makes a fixed panel movable. Dragging (pointer or arrow keys) is claimed only from the panel chrome:
 * the host element itself or a descendant marked `data-drag-handle`. Events from any other descendant,
 * and from interactive controls inside a handle, are left alone.
 */
@Directive({
    selector: "[draggableElement]",
    standalone: true,
    host: { "(window:resize)": "onViewportResize()" },
})
export class DraggableDirective {
    static readonly HandleAttribute = "data-drag-handle";

    step = input<number>(10); // keyboard step size (pixels per keypress)
    /** Shrink the panel instead of letting it extend past the bottom or right edge of the viewport. */
    fitToViewport = input(false);

    private readonly el = inject(ElementRef<HTMLElement>);
    // Signals for position and dragging state
    private x = signal(0);
    private y = signal(0);
    private readonly viewportHeight = signal(window.innerHeight);
    private readonly viewportWidth = signal(window.innerWidth);
    private isDragging = signal(false);
    private startX = signal(0);
    private startY = signal(0);
    private offsetX = signal(0);
    private offsetY = signal(0);

    constructor() {
        // Set initial styles and accessibility attributes
        const element = this.el.nativeElement;
        Object.assign(element.style, {
            position: "fixed",
            zIndex: "1050",
            resize: "both",
        });

        // Make keyboard accessible
        element.setAttribute("tabindex", "0");
        if (!element.hasAttribute("role")) element.setAttribute("role", "group");
        element.setAttribute("aria-label", "Movable panel. Focus the panel edge and use arrow keys to move it.");

        // Initialize position from current location
        const rect = element.getBoundingClientRect();
        this.x.set(rect.left);
        this.y.set(rect.top);
        afterNextRender(() => {
            if (!this.fitToViewport()) return;
            const initialScreenTop = Math.min(this.y(), window.innerHeight * MAX_INITIAL_TOP_FRACTION);
            this.y.set(initialScreenTop - this.screenOffsetY());
        });

        // Effect to update element position when signals change
        effect(() => {
            Object.assign(element.style, {
                left: `${this.x()}px`,
                top: `${this.y()}px`,
            });
            if (!this.fitToViewport()) return;
            element.style.boxSizing = "border-box";
            const rect = element.getBoundingClientRect();
            element.style.maxHeight = `${Math.max(0, this.viewportHeight() - rect.top)}px`;
            element.style.maxWidth = `${Math.max(0, this.viewportWidth() - rect.left)}px`;
        });
    }

    /** True when the event started on the host itself or on a non-interactive part of a drag handle. */
    isChromeTarget(target: EventTarget | null): boolean {
        const host = this.el.nativeElement;
        if (!(target instanceof Element) || !host.contains(target)) return false;
        if (target === host) return true;
        const handle = target.closest(`[${DraggableDirective.HandleAttribute}]`);
        if (!handle || !host.contains(handle)) return false;
        const interactive = target.closest(INTERACTIVE_SELECTOR);
        return !interactive || !handle.contains(interactive) || interactive === handle;
    }

    @HostListener("keydown", ["$event"])
    onKeyDown(event: KeyboardEvent): void {
        // Only handle arrow keys
        if (!["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
            return;
        }
        if (!this.isChromeTarget(event.target)) return;

        event.preventDefault();

        const stepSize = event.shiftKey ? this.step() * 5 : this.step();
        let newX = this.x();
        let newY = this.y();

        switch (event.key) {
            case "ArrowUp":
                newY -= stepSize;
                break;
            case "ArrowDown":
                newY += stepSize;
                break;
            case "ArrowLeft":
                newX -= stepSize;
                break;
            case "ArrowRight":
                newX += stepSize;
                break;
        }

        this.setNewPoint(newX, newY);
    }

    @HostListener("mousedown", ["$event"])
    onMouseDown(event: MouseEvent): void {
        if (!this.isChromeTarget(event.target)) return;

        const computedStyle = getComputedStyle(this.el.nativeElement);
        // Check if the element is resizable, don't want to move the element if the user is trying to resize it
        if (computedStyle.resize !== "none") {
            const rect = this.el.nativeElement.getBoundingClientRect();
            const handleSize = 15;

            // Check if click is in the resize handle area (bottom-right corner)
            const isInResizeHandle =
                event.clientX > rect.right - handleSize && event.clientY > rect.bottom - handleSize;

            if (isInResizeHandle) {
                return; // Let the browser handle resizing
            }
        }

        event.preventDefault();
        this.startDrag(event.clientX, event.clientY);
        this.el.nativeElement.style.cursor = "grabbing";
    }

    @HostListener("document:touchmove", ["$event"])
    @HostListener("document:mousemove", ["$event"])
    onMouseMove(event: MouseEvent | TouchEvent): void {
        if (!this.isDragging()) return;

        event.preventDefault();

        let clientX: number;
        let clientY: number;

        // Duck-type touch: avoid `instanceof TouchEvent` (TouchEvent is undefined on Firefox desktop).
        const touch = "touches" in event && event.touches.length > 0 ? event.touches[0] : undefined;
        if (touch) {
            clientX = touch.clientX;
            clientY = touch.clientY;
        } else {
            clientX = (event as MouseEvent).clientX;
            clientY = (event as MouseEvent).clientY;
        }
        // Calculate new position
        const deltaX = clientX - this.startX();
        const deltaY = clientY - this.startY();

        const newX = this.offsetX() + deltaX;
        const newY = this.offsetY() + deltaY;

        this.setNewPoint(newX, newY);
    }

    @HostListener("document:touchend")
    @HostListener("document:mouseup")
    onMouseUp(): void {
        if (this.isDragging()) {
            this.isDragging.set(false);
            this.el.nativeElement.style.cursor = "";
        }
    }

    // Touch support for mobile
    @HostListener("touchstart", ["$event"])
    onTouchStart(event: TouchEvent): void {
        if (!this.isChromeTarget(event.target)) return;
        event.preventDefault();
        const touch = event.touches[0];
        this.startDrag(touch.clientX, touch.clientY);
    }

    private startDrag(clientX: number, clientY: number) {
        this.isDragging.set(true);
        this.startX.set(clientX);
        this.startY.set(clientY);
        this.offsetX.set(this.x());
        this.offsetY.set(this.y());
    }

    setNewPoint(newX: number, newY: number) {
        const rect = this.el.nativeElement.getBoundingClientRect();
        const maxX = window.innerWidth - rect.width;
        newX = Math.max(0, Math.min(newX, maxX));

        if (this.fitToViewport()) {
            const screenOffsetY = rect.top - this.y();
            const maxY = window.innerHeight - screenOffsetY - Math.min(rect.height, MIN_VISIBLE_HEIGHT);
            newY = Math.max(-screenOffsetY, Math.min(newY, maxY));
        } else {
            newY = Math.max(0, Math.min(newY, window.innerHeight - rect.height));
        }

        this.x.set(newX);
        this.y.set(newY);
    }

    onViewportResize() {
        this.viewportHeight.set(window.innerHeight);
        this.viewportWidth.set(window.innerWidth);
    }

    /** Distance between the CSS `top` and where the panel is drawn (margins, or an ancestor that contains fixed children). */
    private screenOffsetY(): number {
        return this.el.nativeElement.getBoundingClientRect().top - this.y();
    }
}
